import { recordAnalyticsEvent } from "@/lib/analytics.functions";
import { getReferralSource } from "@/lib/referral-tracker";

type Props = Record<string, string | number | boolean | null>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const SESSION_KEY = "welivdit:analytics-session";

/** Stable id for this browser session, used to count unique visitors. */
export function analyticsSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * Fire-and-forget funnel event.
 * Sends to any installed tag manager (gtag / dataLayer) and to our own
 * analytics_events table so conversion can be measured without a 3rd party.
 */
export function trackEvent(event: string, props: Props = {}): void {
  if (typeof window === "undefined") return;

  const enriched: Props = {
    ...props,
    source: getReferralSource(),
    device: window.innerWidth < 768 ? "mobile" : "desktop",
  };

  try {
    window.gtag?.("event", event, enriched);
    window.dataLayer?.push({ event, ...enriched });
  } catch {
    /* tag manager is optional */
  }

  void recordAnalyticsEvent({
    data: {
      event,
      page: window.location.pathname,
      props: enriched,
      sessionId: analyticsSessionId(),
    },
  }).catch(() => {
    /* analytics must never break the UI */
  });
}

/** Records a page view for the growth dashboard. */
export function trackPageView(path: string): void {
  trackEvent("page_view", { path });
}
